import { Chart, Plugin } from 'chart.js';
import { ExtendedChartDataset } from '@app/core/services/charts/chart-data.service';

/**
 * @description Configuration of the {@link createBarBottomCirclesPlugin} instance.
 */
export interface IBarBottomCirclesPluginConfig<T = unknown> {
  /**
   * @description Decides whether a circle is drawn under the bar of a given data point.
   * @param meta The `meta` item of the bound dataset for this index.
   * @param index Index of the data point.
   */
  condition: (meta: T, index: number) => boolean;
  /**
   * @description Fill color of the circles.
   */
  color?: string;
  /**
   * @description Maximum diameter (px) of a circle. The drawn diameter never exceeds this
   * value nor the width of the bar it belongs to. Default value is 6.
   */
  maxDiameter?: number;
  /**
   * @description Gap (px) between the category (x) axis and the top of the circle drawn
   * below it. Default value is 4.
   */
  bottomOffset?: number;
  /**
   * @description Index of the dataset the `meta` values and bars are read from. Default is 0.
   */
  datasetIndex?: number;
}

const DEFAULT_MAX_DIAMETER = 4;
const DEFAULT_BOTTOM_OFFSET = 3;
const DEFAULT_COLOR = '#000000';

/**
 * @description Creates a plugin that draws a filled circle just below the category (x) axis
 * under every bar whose data point satisfies `config.condition`. The circle diameter never
 * exceeds the bar width nor the configured `maxDiameter`, and it is placed `bottomOffset` px
 * below the axis.
 */
export function createBarBottomCirclesPlugin<T = unknown>(config: IBarBottomCirclesPluginConfig<T>): Plugin {
  const {
    condition,
    color = DEFAULT_COLOR,
    maxDiameter = DEFAULT_MAX_DIAMETER,
    bottomOffset = DEFAULT_BOTTOM_OFFSET,
    datasetIndex = 0,
  } = config;

  const drawCircles = (chart: Chart) => {
    if (typeof condition !== 'function') {
      return;
    }

    const xScale = chart.scales['x'];
    if (!xScale) {
      return;
    }

    const dataset = chart.data.datasets?.[datasetIndex] as ExtendedChartDataset<T> | undefined;
    const meta = chart.getDatasetMeta(datasetIndex);
    const metaData = dataset?.meta;

    if (!dataset || !meta?.data?.length || !Array.isArray(metaData)) {
      return;
    }

    const points = meta.data;
    const count = Math.min(points.length, metaData.length);
    // Top edge of the x scale is the axis line (baseline of the bars). Circles sit below it.
    const axisY = xScale.top;
    const { left, right } = chart.chartArea;

    const ctx = chart.ctx;
    ctx.save();
    // Clip to the plot area x-range so circles of bars scrolled out (pan/zoom) disappear
    // together with their bars.
    ctx.beginPath();
    ctx.rect(left, axisY, right - left, bottomOffset + maxDiameter);
    ctx.clip();
    ctx.fillStyle = color;

    for (let i = 0; i < count; i++) {
      if (!condition(metaData[i], i)) {
        continue;
      }

      const bar = points[i] as unknown as { x: number; width: number };
      const barWidth = Number.isFinite(bar.width) ? bar.width : maxDiameter;
      // Diameter is capped by both the bar width and the configured maximum.
      const diameter = Math.min(maxDiameter, barWidth);
      if (!(diameter > 0) || !Number.isFinite(bar.x)) {
        continue;
      }

      const radius = diameter / 2;
      const centerY = axisY + bottomOffset + radius;

      ctx.beginPath();
      ctx.arc(bar.x, centerY, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  };

  return {
    id: 'barBottomCircles',
    afterDatasetsDraw: drawCircles,
  };
}
